import { useState } from "react";
import Input from "../../../components/FormComponents/Input";
import Label from "../../../components/FormComponents/Label";
import Button from "../../../components/FormComponents/Button";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import ButtonActionIcon from "../../../components/BuilderComponents/ButtonActionIcon";
import BuilderModal from "../../../Layouts/Modal/BuilderModal";
import CodeEditor from "../../../components/BuilderComponents/CodeEditor/CodeEditor";
import { ReactSortable } from "react-sortablejs";

const HttpReqBodyDefinition = ({ selectedApiData, setSelectedApiData }) => {
  const handleChange = (key, value) => {
    setSelectedApiData({ ...selectedApiData, [key]: value });
  };
  return (
    <>
      {selectedApiData?.body_option === "form-data" ? (
        <FormDataBodyDefinition
          values={selectedApiData?.body_form_data || []}
          onChange={(data) => handleChange("body_form_data", data)}
        />
      ) : selectedApiData?.body_option === "raw-key-value" || selectedApiData?.body_option === "url-encoded" ? (
        <RawKeyValueBodyDefinition
          values={selectedApiData?.body_raw_key_values || []}
          onChange={(data) => handleChange("body_raw_key_values", data)}
        />
      ) : (
        <RawBodyDataDefinition
          value={selectedApiData?.body_data_definition || ""}
          onChange={(value) => handleChange("body_data_definition", value)}
        />
      )}
    </>
  );
};

const defaultRawKeyValue = { key: "", value: "" };

const CreateRawKeyValue = ({ onCreate }) => {
  const [newField, setNewField] = useState(defaultRawKeyValue);

  const handleAdd = () => {
    if (!newField.key.trim()) return;
    onCreate(newField);
    setNewField(defaultRawKeyValue);
  };

  return (
    <div className="d_flex align_center p_3 border_full gap_3 mb_3">
      <Input
        placeholder="Enter Key"
        value={newField.key}
        onChange={(e) => setNewField({ ...newField, key: e.target.value })}
      />
      <Input
        placeholder="Enter Value"
        value={newField.value}
        onChange={(e) => setNewField({ ...newField, value: e.target.value })}
      />
      <Button onClick={handleAdd}>Add</Button>
    </div>
  );
};

const RawKeyValueBodyDefinition = ({ values, onChange }) => {
  const handleAdd = (newField) => {
    const updated = [...(values || []), newField];
    onChange(updated);
  };

  const handleUpdate = (index, key, value) => {
    const updated = [...(values || [])];
    updated[index][key] = value;
    onChange(updated);
  };

  const handleDelete = (index) => {
    const updated = [...(values || [])];
    updated.splice(index, 1);
    onChange(updated);
  };

  return (
    <div className="mb_2 flex_column gap_1">
      <Label>Raw Key-Value:</Label>
      <CreateRawKeyValue onCreate={handleAdd} />
      <div className="border_full">
        <div className="d_flex align_center gap_2 mb_2 bg_gray font_weight_semibold">
          <div className="w_auto ml_1" style={{ width: "24px", height: "24px" }} /> {/* Placeholder for drag icon */}
          <div className="w_100">Key</div>
          <div className="w_100">Value</div>
          <Button variant="delete" className="opacity_0">
            <MaterialIcon name="delete" />
          </Button>
        </div>

        <div className="p-2">
          <ReactSortable list={values} setList={(newOrder) => onChange(newOrder)} animation={200} handle=".drag_handle">
            {values.map((item, index) => (
              <div key={item.id || index} className="d_flex align_center gap_2 mb_2">
                <div className="drag_handle cursor_move mr_1">
                  <MaterialIcon name="drag_indicator" />
                </div>
                <Input
                  value={item.key}
                  onChange={(e) => handleUpdate(index, "key", e.target.value)}
                  placeholder="Key"
                />
                <Input
                  value={item.value}
                  onChange={(e) => handleUpdate(index, "value", e.target.value)}
                  placeholder="Value"
                />
                <Button variant="delete" onClick={() => handleDelete(index)}>
                  <MaterialIcon name="delete" />
                </Button>
              </div>
            ))}
          </ReactSortable>
        </div>
      </div>
    </div>
  );
};

const defaultFormDataKeyValue = {
  key: "",
  type: "text",
  value: ""
};
const CreateFormDataKeyValue = ({ onCreate }) => {
  const [newField, setNewField] = useState(defaultFormDataKeyValue);
  const handleAdd = () => {
    onCreate(newField);
    setNewField(defaultFormDataKeyValue);
  };
  return (
    <div className="d_flex align_center p_3 border_full gap_3 mb_3">
      <Input
        placeholder="Enter Key"
        value={newField.key}
        onChange={(e) => setNewField({ ...newField, key: e.target.value })}
      />
      <select
        value={newField.type}
        onChange={(e) => setNewField({ ...newField, type: e.target.value })}
        className={"builder_input"}
      >
        <option value="text">Text</option>
        <option value="file">File</option>
      </select>
      <Input
        placeholder="Enter Value"
        value={newField.value}
        onChange={(e) => setNewField({ ...newField, value: e.target.value })}
      />
      <Button onClick={handleAdd}>Add</Button>
    </div>
  );
};

const FormDataBodyDefinition = ({ values, onChange }) => {
  const handleAdd = (newField) => {
    if (!newField.key.trim()) return;

    const newEntry = { ...newField };
    const updated = [...(values || []), newEntry];

    onChange(updated);
  };

  const handleUpdate = (index, key, value) => {
    const updated = [...(values || [])];
    updated[index][key] = value;
    onChange(updated);
  };

  const handleDelete = (index) => {
    const updated = [...(values || [])];
    updated.splice(index, 1);
    onChange(updated);
  };

  return (
    <div className={"mb_2 flex_column gap_1"}>
      <Label>Form Data:</Label>
      <CreateFormDataKeyValue onCreate={handleAdd} />
      <div className={"border_full"}>
        <div className="d_flex align_center gap_2 mb_2 bg_gray font_weight_semibold">
          <div className={"w_100 ml_2"}>Key</div>
          <div className={"w_100"}>Type</div>
          <div className={"w_100 ml_1"}>Value</div>
          <Button variant={"delete"} className={"opacity_0"}>
            <MaterialIcon name="delete" />
          </Button>
        </div>
        <div className={"p-2"}>
          {values.map((item, index) => (
            <div key={index} className="d_flex align_center gap_2 mb_2">
              <Input value={item.key} onChange={(e) => handleUpdate(index, "key", e.target.value)} placeholder="Key" />
              <select
                value={item.type}
                className={"builder_input"}
                onChange={(e) => handleUpdate(index, "type", e.target.value)}
              >
                <option value="text">Text</option>
                <option value="file">File</option>
              </select>
              <Input
                value={item.value}
                onChange={(e) => handleUpdate(index, "value", e.target.value)}
                placeholder="Value"
              />
              <Button variant="delete" onClick={() => handleDelete(index)}>
                <MaterialIcon name="delete" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const initialModalStateForBody = {
  isOpen: false,
  modalName: null
};
const RawBodyDataDefinition = ({ value, onChange }) => {
  const [modalState, setModalState] = useState(initialModalStateForBody);

  const openModal = (name) => {
    setModalState({ isOpen: true, modalName: name });
  };

  const closeModal = () => {
    setModalState(initialModalStateForBody);
  };

  return (
    <div className="mb_3 d_flex flex_column gap_2">
      <div className="d_flex space_between pb_1">
        <Label>Body Definition:</Label>
        <div className="d_flex align_center gap_3 ml_auto">
          <ButtonActionIcon type="fullscreen" title="Fullscreen" onClick={() => openModal("Edit Body Definition")} />
        </div>
      </div>

      <textarea className="builder_input" value={value} onChange={(e) => onChange(e.target.value)} rows={6} />
      <BuilderModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        modalName="Body Definition Editor"
        bodyStyle={{ width: "70vw", height: "80vh" }}
      >
        <CodeEditor height="75vh" value={value} fieldObj={{}} handleChange={onChange} enableBacktickTrigger={false} />
      </BuilderModal>
    </div>
  );
};

export default HttpReqBodyDefinition;
